import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { CardLayout, MTable } from "../v3";
import { workerReportColumns } from "../../pages/report/constants";
import { get_date } from "../generalComponets";
import { AnalyticsService } from "../../services";
import { downloadFile } from "../../utils/download.utils";

export interface WorkerReportResultsProps {
  reportType: string;
  data?: any;
  loading?: boolean;
  paginationState: any;
  handlePaginationChange?: (model: any) => void;
  reportFilters?: any;
}

export const WorkerReportResults = ({
  reportType,
  data = {},
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters = {},
}: WorkerReportResultsProps) => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [prevFilters, setPrevFilters] = useState({});

  useEffect(() => {
    if (pdfSuccess && JSON.stringify(reportFilters) !== JSON.stringify(prevFilters)) {
      setPdfSuccess(false);
    }
  }, [reportFilters, prevFilters, pdfSuccess]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    setPdfSuccess(false);
    try {
      const response = await AnalyticsService.downloadWorkerReportPdf(reportFilters || {});

      const currentDateTime = moment().format("YYYYMMDD-HHmmss");
      const filename = `worker-report-${reportType}-${currentDateTime}.pdf`;

      downloadFile(response.data, filename);
      toast.success("Report downloaded successfully");

      setPdfSuccess(true);
      setPrevFilters(reportFilters);
    } catch (error) {
      console.error("Report download failed:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to download report");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const rows =
    data?.rows?.map((row, index) => {
      const isGroupWork = Boolean(row.isGroupWork);
      return {
        id: index + 1 + paginationState.page * paginationState.pageSize,
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        formattedDate: get_date(row.date),
        remark: row?.projectName || row?.description,
        ...row,
        wage: isGroupWork ? row.totalAmount : row.wage,
        overtime: isGroupWork ? 0 : row.overtime,
        type: row.type === "transaction" ? "Payment" : isGroupWork ? "Group" : "Individual",
      };
    }) || [];

  const totalOpeningBalance = rows.reduce((sum, row) => sum + Number(row.openingBalance || 0), 0);
  const totalWorkedWage = rows.reduce((sum, row) => sum + Number(row.totalWage || 0), 0);
  const totalOvertime = rows.reduce((sum, row) => sum + Number(row.totalOvertime || 0), 0);
  const totalPaid = rows.reduce((sum, row) => sum + Number(row.totalPaid || 0), 0);
  const totalClosingBalance = rows.reduce((sum, row) => sum + Number(row.closingBalance || 0), 0);
  const totalDailyWage = rows.reduce((sum, row) => sum + Number(row.wage || 0), 0);
  const totalDailyOvertime = rows.reduce((sum, row) => sum + Number(row.overtime || 0), 0);
  const totalDailyPaid = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const subtotal =
    data?.count > 0
      ? [
          {
            id: "subtotal",
            label: "Subtotal",
            totalOpeningBalance: totalOpeningBalance.toFixed(2),
            totalWorkedWage: totalWorkedWage.toFixed(2),
            totalOvertime: totalOvertime.toFixed(2),
            totalPaid: totalPaid.toFixed(2),
            totalClosingBalance: totalClosingBalance.toFixed(2),
            totalDailyWage: totalDailyWage.toFixed(2),
            totalDailyOvertime: totalDailyOvertime.toFixed(2),
            totalDailyPaid: totalDailyPaid.toFixed(2),
          },
        ]
      : [];

  const allRows = [...rows, ...subtotal];
  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton // TODO: implement permission
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={workerReportColumns[reportType]}
        tableRows={allRows}
        rowCount={data.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
